import React from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';

const FollowUpStatusTracker = ({ approval, currentStatus, onStatusChange }) => {
  const statusOptions = [
    { value: 'pending-review', label: 'Pending Review' },
    { value: 'demo-scheduled', label: 'Demo Scheduled' },
    { value: 'demo-completed', label: 'Demo Completed' },
    { value: 'in-negotiation', label: 'In Negotiation' },
    { value: 'proposal-sent', label: 'Proposal Sent' },
    { value: 'closed-won', label: 'Closed - Won' },
    { value: 'closed-lost', label: 'Closed - Lost' },
    { value: 'on-hold', label: 'On Hold' },
  ];

  const getStatusConfig = (status) => {
    const configs = {
      'pending-review': {
        icon: 'Clock',
        color: 'text-warning',
        bg: 'bg-warning/10',
        border: 'border-warning/20'
      },
      'demo-scheduled': {
        icon: 'Calendar',
        color: 'text-primary',
        bg: 'bg-primary/10',
        border: 'border-primary/20'
      },
      'demo-completed': {
        icon: 'CheckCircle',
        color: 'text-accent',
        bg: 'bg-accent/10',
        border: 'border-accent/20'
      },
      'in-negotiation': {
        icon: 'MessageSquare',
        color: 'text-secondary',
        bg: 'bg-secondary/10',
        border: 'border-secondary/20'
      },
      'proposal-sent': {
        icon: 'Send',
        color: 'text-primary',
        bg: 'bg-primary/10',
        border: 'border-primary/20'
      },
      'closed-won': {
        icon: 'CheckCircle2',
        color: 'text-success',
        bg: 'bg-success/10',
        border: 'border-success/20'
      },
      'closed-lost': {
        icon: 'XCircle',
        color: 'text-error',
        bg: 'bg-error/10',
        border: 'border-error/20'
      },
      'on-hold': {
        icon: 'Pause',
        color: 'text-muted-foreground',
        bg: 'bg-muted',
        border: 'border-border'
      }
    };
    return configs?.[status] || configs?.['pending-review'];
  };

  const config = getStatusConfig(currentStatus);

  return (
    <div className="bg-card rounded-2xl p-4 md:p-6 border border-border">
      <div className="flex items-center gap-2 mb-4">
        <Icon name="TrendingUp" size={20} className="text-primary" />
        <h4 className="text-lg font-semibold text-foreground">Follow-Up Status</h4>
      </div>
      <div className="space-y-4">
        <div className={`p-4 rounded-xl border ${config?.bg} ${config?.border}`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg bg-background/50`}>
              <Icon name={config?.icon} size={20} className={config?.color} />
            </div>
            <div className="flex-1">
              <p className="text-xs text-muted-foreground mb-1">Current Status</p>
              <p className={`text-sm font-semibold ${config?.color}`}>
                {statusOptions?.find(opt => opt?.value === currentStatus)?.label || 'Pending Review'}
              </p>
            </div>
          </div>
        </div>

        <Select
          label="Update Status"
          placeholder="Select new status"
          options={statusOptions}
          value={currentStatus || 'pending-review'}
          onChange={(value) => onStatusChange(approval?.id, value)}
        />

        <div className="bg-muted/50 rounded-xl p-4">
          <h5 className="text-sm font-medium text-foreground mb-3">Status Pipeline</h5>
          <div className="space-y-2">
            {[
              { status: 'pending-review', label: 'Pending Review' },
              { status: 'demo-scheduled', label: 'Demo Scheduled' },
              { status: 'demo-completed', label: 'Demo Completed' },
              { status: 'in-negotiation', label: 'In Negotiation' },
              { status: 'proposal-sent', label: 'Proposal Sent' },
              { status: 'closed-won', label: 'Closed - Won' },
            ]?.map((stage, index) => {
              const stageConfig = getStatusConfig(stage?.status);
              const isActive = currentStatus === stage?.status;
              const isPast = statusOptions?.findIndex(opt => opt?.value === currentStatus) > index;
              
              return (
                <div
                  key={stage?.status}
                  className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${
                    isActive ? 'bg-primary/5' : ''
                  }`}
                >
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center ${
                      isActive || isPast ? stageConfig?.bg : 'bg-muted'
                    }`}
                  >
                    {isPast ? (
                      <Icon name="Check" size={14} className="text-success" />
                    ) : (
                      <span
                        className={`w-2 h-2 rounded-full ${
                          isActive ? stageConfig?.color?.replace('text-', 'bg-') : 'bg-muted-foreground/30'
                        }`}
                      />
                    )}
                  </div>
                  <span
                    className={`text-sm ${
                      isActive ? 'font-medium text-foreground' : 'text-muted-foreground'
                    }`}
                  >
                    {stage?.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FollowUpStatusTracker;